import { useEffect, useState } from 'react';
import { IonButton, IonIcon, IonInput, IonItem, IonSpinner } from '@ionic/react';
import { arrowForwardOutline, notificationsOutline, checkmarkCircle } from 'ionicons/icons';
import { Card, Page } from '../components/Shell';
import { MapPicker } from '../components/MapPicker';
import { PlaceSelector } from '../components/PlaceSelector';
import { api } from '../lib/api';
import { enablePush, pushSupport } from '../lib/push';
import { getCurrentPosition } from '../lib/tracking';
import type { Coords, Ride } from '../lib/types';
import type { Session } from '../lib/session';

interface Props {
  initialCode?: string;
  onJoined: (session: Session, ride: Ride) => void;
  onReady: () => void;
  onBack: () => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
  session: Session | null;
  ride: Ride | null;
}

export function PassengerJoin({
  initialCode,
  onJoined,
  onReady,
  onBack,
  onError,
  onNotice,
  session,
  ride,
}: Props) {
  const [code, setCode] = useState(initialCode ?? '');
  const [joining, setJoining] = useState(false);
  const [pickup, setPickup] = useState<Coords | null>(null);
  const [savingPickup, setSavingPickup] = useState(false);
  const [pushReady, setPushReady] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushHint, setPushHint] = useState<string | null>(null);

  const joined = session !== null && ride !== null;

  // Nach dem Beitritt gleich den eigenen Standort als Abholpunkt vorschlagen.
  useEffect(() => {
    if (!joined || pickup) return;
    if (ride?.pickup) {
      setPickup(ride.pickup);
      return;
    }
    getCurrentPosition()
      .then((fix) => setPickup((current) => current ?? { lat: fix.lat, lng: fix.lng }))
      .catch(() => { /* dann eben von Hand auf der Karte */ });
  }, [joined, ride, pickup]);

  useEffect(() => {
    if (pushSupport() === 'granted' && ride?.hasPassengerSubscription) setPushReady(true);
  }, [ride]);

  async function join() {
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length < 4) {
      onError('Bitte gib den sechsstelligen Code ein.');
      return;
    }
    setJoining(true);
    try {
      const { ride: fresh, token } = await api.join(trimmed);
      onJoined({ rideId: fresh.id, token, role: 'passenger' }, fresh);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Beitritt fehlgeschlagen.');
    } finally {
      setJoining(false);
    }
  }

  async function savePickup(): Promise<boolean> {
    if (!session || !pickup) return false;
    setSavingPickup(true);
    try {
      // Die aktualisierte Fahrt gleich weiterreichen - sonst startet die
      // Wartesicht mit dem alten Stand und behauptet, es sei noch kein
      // Abholpunkt gesetzt.
      const { ride: fresh } = await api.setPickup(session.rideId, session.token, pickup);
      onJoined(session, fresh);
      return true;
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Abholpunkt konnte nicht gesetzt werden.');
      return false;
    } finally {
      setSavingPickup(false);
    }
  }

  async function turnOnNotifications() {
    if (!session) return;
    setPushBusy(true);
    try {
      const result = await enablePush(session.rideId, session.token);
      if (result.state === 'granted') {
        setPushReady(true);
        setPushHint(null);
        await api.testPush(session.rideId, session.token);
        onNotice('Benachrichtigungen aktiv - eine Testnachricht ist unterwegs.');
      } else {
        setPushHint(result.message ?? 'Benachrichtigungen sind hier nicht verfügbar.');
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Benachrichtigungen fehlgeschlagen.');
    } finally {
      setPushBusy(false);
    }
  }

  async function finish() {
    if (await savePickup()) onReady();
  }

  if (!joined) {
    return (
      <Page title="Mitfahren" onBack={onBack}>
        <Card>
          <h2>Code eingeben</h2>
          <p>Den sechsstelligen Code bekommst du von der Person, die dich abholt.</p>
          <IonItem lines="none" style={{ marginTop: 12 }}>
            <IonInput
              label="Fahrtcode"
              labelPlacement="stacked"
              placeholder="ABC123"
              maxlength={6}
              autocapitalize="characters"
              value={code}
              className="r2f-codeinput"
              onIonInput={(event) => setCode((event.detail.value ?? '').toUpperCase())}
            />
          </IonItem>
        </Card>

        <div className="r2f-actionbar">
          <IonButton expand="block" size="large" onClick={join} disabled={joining}>
          {joining ? (
            <IonSpinner name="dots" />
          ) : (
            <>
              Beitreten
              <IonIcon slot="end" icon={arrowForwardOutline} />
            </>
          )}
          </IonButton>
        </div>
      </Page>
    );
  }

  return (
    <Page title="Fast fertig" onBack={onBack}>
      <Card>
        <h2>Wo sollst du abgeholt werden?</h2>
        <p>
          Von diesem Punkt aus rechnen wir, wie lange {ride?.driverName ?? 'der Fahrer'} noch
          braucht.
        </p>
        <div style={{ marginTop: 12 }}>
          <PlaceSelector value={pickup} onChange={setPickup} onError={onError} />
        </div>
      </Card>

      <Card flush>
        <MapPicker value={pickup} onChange={setPickup} kind="pickup" />
      </Card>

      <Card>
        <h2>Benachrichtigungen</h2>
        <p>
          Ohne sie musst du die App offen halten. Mit ihnen bekommst du Bescheid, auch wenn
          das Handy in der Tasche steckt.
        </p>
        <div style={{ marginTop: 12 }}>
          {pushReady ? (
            <div className="r2f-pill r2f-pill--live">
              <IonIcon icon={checkmarkCircle} />
              Benachrichtigungen sind aktiv
            </div>
          ) : (
            <IonButton expand="block" fill="outline" onClick={turnOnNotifications} disabled={pushBusy}>
              {pushBusy ? (
                <IonSpinner name="dots" />
              ) : (
                <>
                  <IonIcon slot="start" icon={notificationsOutline} />
                  Benachrichtigungen einschalten
                </>
              )}
            </IonButton>
          )}
        </div>
        {pushHint && <div className="r2f-hint" style={{ marginTop: 12 }}>{pushHint}</div>}
      </Card>

      <div className="r2f-actionbar">
        <IonButton expand="block" size="large" onClick={finish} disabled={!pickup || savingPickup}>
          {savingPickup ? <IonSpinner name="dots" /> : 'Los geht’s'}
        </IonButton>
      </div>
    </Page>
  );
}
